import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useDenyWarEnabled } from "../../../hooks/useDenyWarEnabled";
import { GET_TOP_DENIERS } from "../../../queries/user_queries";
import { DateRange } from "../../../types/Util";
import { truncate } from "../../../util/StringUtil";
import Loading from "../../Loading";
import DashCard from "./DashCard";

const DENY_WAR_RANGE = DateRange.DENY_WAR.toLowerCase();

const TopDenyCard = () => {
  const [selectedRange, setSelectedRange] = useState(
    DateRange.MONTH.toLowerCase()
  );
  const { loading, error, data } = useQuery(GET_TOP_DENIERS, {
    variables: { range: selectedRange },
  });
  const { isDenyWarEnabled } = useDenyWarEnabled();

  // Fall back to the default range if wars get turned off while it's selected.
  useEffect(() => {
    if (!isDenyWarEnabled && selectedRange === DENY_WAR_RANGE) {
      setSelectedRange(DateRange.MONTH.toLowerCase());
    }
  }, [isDenyWarEnabled, selectedRange]);

  if (!data || !data.getTopDeniers) {
    return <Loading />;
  }
  const { topDeniers, topUndeniers, totalCounts } = data.getTopDeniers;

  const { totalDenierCount, totalUndenierCount } = totalCounts;
  // Scoping to the latest war only makes sense while wars are running.
  const rangeOptions = Object.values(DateRange).filter(
    (r) => isDenyWarEnabled || r !== DateRange.DENY_WAR
  );
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRange(event.target.value as DateRange); // Cast to Color enum type
  };
  // Literal class strings: Tailwind only emits classes it can see.
  const TONES = {
    error: { text: "text-error", divider: "divider divider-error m-0" },
    success: { text: "text-success", divider: "divider divider-success m-0" },
  } as const;
  const side = (
    label: string,
    total: number,
    rows: any[],
    tone: "error" | "success",
  ) => (
    <div className="min-w-0">
      <div className={`text-xl font-bold ${TONES[tone].text}`}>
        {total.toFixed(1)} {label}
      </div>
      <div className={TONES[tone].divider} />
      {rows.length === 0 ? (
        <div className="text-sm text-base-content/60 py-2">Nobody yet.</div>
      ) : (
        <ul className="max-h-48 overflow-y-auto pr-1 text-sm">
          {rows.map((d: any, i: number) => (
            <li
              key={d.username}
              className={`flex justify-between py-0.5 ${i === 0 ? `${TONES[tone].text} font-semibold` : ""}`}
            >
              <span className="truncate">{truncate(d.username, 20)}</span>
              <span>{d.count.toFixed(1)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <DashCard
      title="Deny vs undeny"
      className="md:col-span-2 xl:col-span-3"
      aside={
        <select
          className="select select-bordered select-sm"
          value={selectedRange}
          onChange={handleChange}
          aria-label="Range"
        >
          {rangeOptions.map((r) => (
            <option key={r} value={r.toLowerCase()}>
              {r}
            </option>
          ))}
        </select>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {side("denies", totalDenierCount, topDeniers, "error")}
        {side("undenies", totalUndenierCount, topUndeniers, "success")}
      </div>
    </DashCard>
  );
};

export default TopDenyCard;
